import { ITelemetryItem } from '@microsoft/applicationinsights-web';

/** Placeholder written in place of any redacted value. */
export const REDACTED = '[REDACTED]';

/**
 * Header names (lower-cased) whose values must never reach App Insights.
 * Includes the app's custom auth headers, not just the well-known ones.
 */
export const SENSITIVE_HEADERS: ReadonlyArray<string> = [
  'authorization',
  'cookie',
  'set-cookie',
  'epc-token',
  'x-api-key',
  'api-key',
  'x-auth-token',
  'proxy-authorization',
];

/** Query-string parameters (lower-cased) that may carry secrets or PII. */
export const SENSITIVE_QUERY_PARAMS: ReadonlyArray<string> = [
  'access_token',
  'id_token',
  'refresh_token',
  'token',
  'code',
  'sas',
  'sig',
  'signature',
  'password',
  'secret',
  'email',
];

/**
 * Dependency hosts that are pure noise in RUM: third-party beacons/pixels and
 * the telemetry ingestion endpoints themselves. Matched as substrings.
 */
export const NOISE_DEPENDENCY_HOSTS: ReadonlyArray<string> = [
  'applicationinsights.azure.com',
  'monitor.azure.com',
  'dc.services.visualstudio.com',
  'sentry.io',
  'googletagmanager.com',
  'google-analytics.com',
  'analytics.google.com',
  'doubleclick.net',
  'intercom.io',
  'intercomcdn.com',
  'productfruits.com',
  'service-now.com',
];

/**
 * Removes the values of sensitive query-string parameters from a URL while
 * keeping the rest of the URL intact. Returns the input unchanged if it cannot
 * be parsed as a URL.
 * @param url
 */
export function scrubUrl(url: string): string {
  if (!url) {
    return url;
  }

  const queryIndex = url.indexOf('?');
  if (queryIndex === -1) {
    return url;
  }

  const base = url.slice(0, queryIndex);
  const query = url.slice(queryIndex + 1);

  const scrubbed = query
    .split('&')
    .map((pair) => {
      const eqIndex = pair.indexOf('=');
      const key = eqIndex === -1 ? pair : pair.slice(0, eqIndex);
      if (SENSITIVE_QUERY_PARAMS.includes(key.toLowerCase())) {
        return `${key}=${REDACTED}`;
      }
      return pair;
    })
    .join('&');

  return `${base}?${scrubbed}`;
}

/**
 * Redacts the values of sensitive headers within a header bag (string or object
 * form), mutating it in place.
 * @param headers
 */
function scrubHeaders(headers: unknown): void {
  if (!headers || typeof headers !== 'object') {
    return;
  }

  const bag = headers as Record<string, unknown>;
  Object.keys(bag).forEach((key) => {
    if (SENSITIVE_HEADERS.includes(key.toLowerCase())) {
      bag[key] = REDACTED;
    }
  });
}

/**
 * Returns true when a dependency target points at a noise host.
 * @param value
 */
function isNoiseDependency(value: string | undefined): boolean {
  if (!value) {
    return false;
  }
  const lower = value.toLowerCase();
  return NOISE_DEPENDENCY_HOSTS.some((host) => lower.includes(host));
}

/**
 * App Insights telemetry initializer that scrubs PII/secrets and drops noisy
 * dependencies. Registered via `addTelemetryInitializer`.
 *
 * - Drops dependency telemetry pointing at telemetry/third-party beacons.
 * - Redacts sensitive query-string parameters from request/dependency URLs.
 * - Redacts sensitive request/response headers when header tracking is enabled.
 *
 * Returning `false` discards the telemetry item; returning nothing keeps it.
 * @param item
 */
export function scrubTelemetry(item: ITelemetryItem): boolean | void {
  const baseData = item?.baseData;
  if (!baseData) {
    return;
  }

  const isDependency = item.baseType === 'RemoteDependencyData';

  if (
    isDependency &&
    (isNoiseDependency(baseData.target) || isNoiseDependency(baseData.name))
  ) {
    return false;
  }

  // Scrub URL-bearing fields (dependency `data`/`target`/`name`, request `url`).
  (['data', 'target', 'name', 'url'] as const).forEach((field) => {
    const value = baseData[field];
    if (typeof value === 'string') {
      baseData[field] = scrubUrl(value);
    }
  });

  // Redact tracked headers (the ajax plugin stores them under properties).
  const properties = baseData.properties as Record<string, unknown> | undefined;
  if (properties) {
    scrubHeaders(properties.requestHeaders);
    scrubHeaders(properties.responseHeaders);
  }
}
